import { Checkbox } from "@choose/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@choose/ui/command";
import { useTranslation } from "react-i18next";

import {
  SupportRole,
  useCurrentUserQuery,
  useInternalUsersQuery,
} from "~/graphql/graphql.gen";
import type { InternalUser } from "~/stores/users-store";
import {
  ASSIGN_MATE_USER,
  getNoAssigneeUser,
  mapAssigneeToList,
} from "~/stores/users-store";

export function AssigneeSelector({
  multiple = false,
  onSelect,
  value,
}: {
  multiple?: boolean;
  onSelect: (userId: string, closeModal: boolean) => void;
  value: string[];
}) {
  const { t } = useTranslation();
  const { data: agents } = useInternalUsersQuery(
    {
      filters: {
        role: {
          in: [
            SupportRole.Admin,
            SupportRole.Agent,
            SupportRole.OperatorManager,
          ],
        },
      },
    },
    {
      select: (data) => data.internalUsers,
    },
  );
  const { data: user } = useCurrentUserQuery(undefined, {
    select: (data) => data.currentUser,
  });

  const noAssigneeUser = getNoAssigneeUser(t);

  const suggestedUsers: InternalUser[] =
    user?.__typename === "ActorInternalUser" ? [user] : [];

  const users = (agents ?? [])
    .filter((user) => !suggestedUsers.map((user) => user.id).includes(user.id))
    .toSorted(
      (a, b) =>
        a.internalUser.firstName.localeCompare(b.internalUser.firstName) -
        b.internalUser.firstName.localeCompare(a.internalUser.firstName),
    );
  return (
    <Command>
      <CommandInput placeholder={t("tickets.filters.assignee.placeholder")} />
      <CommandList>
        <CommandEmpty>{t("tickets.filters.assignee.empty")}</CommandEmpty>
        <CommandGroup>
          {[
            noAssigneeUser,
            ASSIGN_MATE_USER,
            ...mapAssigneeToList(suggestedUsers),
            ...mapAssigneeToList(users),
          ].map((item) => (
            <CommandItem
              key={item.value}
              value={item.value}
              onSelect={() => {
                onSelect(item.value, true);
              }}
              className="flex items-center justify-between"
            >
              <div className="flex items-center gap-2">
                {multiple ? (
                  <Checkbox
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelect(item.value, false);
                    }}
                    checked={value.includes(item.value)}
                    // Fixes shadcn overriding with data-selected:**:[svg]:text-foreground
                    className="data-checked:**:[svg]:text-primary-foreground!"
                  />
                ) : null}
                <div className="flex items-center gap-2">
                  <item.icon />
                  {item.label}
                </div>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}
